import { Card, Box, Stack, Typography, Tooltip, useMediaQuery, Theme } from '@mui/material';
import { colors } from '../../../ui/colors';
import { Project } from '../types/dashboard.types';
import { formatTimeAgo } from '../helpers/time-ago.helper';
import { StatusChip } from './StatusChip';
import { ProjectCardMenu } from './ProjectCardMenu';

interface ProjectListProps {
  projects: Project[];
  onOpen: (projectId: string, projectType: "draft" | "publish") => void;
  onRename: (projectId: string, projectName: string) => void;
  onDuplicate: (projectId: string) => void;
  onDelete: (projectId: string, projectName: string) => void;
}

const ProjectListItem: React.FC<
  {
    project: Project;
  } & Omit<ProjectListProps, 'projects'>
> = ({ project, onOpen, onRename, onDuplicate, onDelete }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  return (
    <Card
      onClick={() => onOpen(project.id, project.status)}
      sx={{
        padding: 2,
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: 'var(--fx-surface-base, #FFFFFF)',
        border: '1px solid var(--fx-surface-border, #E2E8F0)',
        '&:hover': {
          borderColor: colors.accent || '#ff9500',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        {/* Thumbnail */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
            flexShrink: 0,
            display: isSmallScreen ? 'none' : 'block',
          }}
        />

        {/* Info */}
        <Stack
          spacing={0.5}
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Tooltip title={project.projectName}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'var(--fx-surface-text, #0F172A)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {project.projectName}
            </Typography>
          </Tooltip>

          <Tooltip title={project.slug}>
            <Typography
              variant="caption"
              sx={{
                color: 'var(--fx-surface-textsecondary, #64748B)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {project.slug}
            </Typography>
          </Tooltip>
        </Stack>

        {/* Status and Template */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 'fit-content',
          }}
        >
          {!isSmallScreen && (
            <>
              <StatusChip status={project.status} size="small" />

              <Tooltip title={`Template: ${project.templateId}`}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.accent || '#ff9500',
                    fontWeight: 500,
                    minWidth: 100,
                  }}
                >
                  ⭐ {project.templateId}
                </Typography>
              </Tooltip>

              <Typography
                variant="caption"
                sx={{
                  color: 'var(--fx-surface-texttertiary, #94A3B8)',
                  minWidth: 80,
                  textAlign: 'right',
                }}
              >
                {formatTimeAgo(project.updatedAt)}
              </Typography>
            </>
          )}

          <Box onClick={(e) => e.stopPropagation()}>
            <ProjectCardMenu
              projectStatus={project.status}
              projectId={project.id}
              projectName={project.projectName}
              onOpen={onOpen}
              onRename={onRename}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
}) => {
  return (
    <Stack spacing={1}>
      {projects.map((project) => (
        <ProjectListItem
          key={project.id}
          project={project}
          onOpen={onOpen}
          onRename={onRename}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      ))}
    </Stack>
  );
};
